import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ArrowLeft, Trash2, Search, Save, RotateCcw, Pencil, XCircle } from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

export default function LicenseRemarksListing() {
  const nav = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const finYearId = localStorage.getItem("finYearId") || "";
  const userId = localStorage.getItem("loginuserid") || localStorage.getItem("userId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const employeeId = localStorage.getItem("LicEmployeeID") || ""; // set from employee listing "Remarks"

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // Search + Pagination
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  // Select for delete
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Inline add/edit form (ONLY 2 fields)
  const [mode, setMode] = useState("add"); // add | edit
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [saving, setSaving] = useState(false);

  // ---- styles
  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-bold text-white shadow-lg transition-all duration-200 ` +
    `bg-red-600 hover:bg-red-700`;

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-bold text-white shadow-lg transition-all duration-200 ` +
    `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`;

  const secondaryBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-bold text-white shadow-lg transition-all duration-200 ` +
    `bg-gray-600 hover:bg-gray-700`;

  // ---- helpers
  const resetForm = () => {
    setMode("add");
    setEditingId(null);
    setTitle("");
    setDetails("");
  };

  const validate = () => {
    if (!title.trim()) {
      toast.warn("Please Enter Title..");
      return false;
    }
    if (!details.trim()) {
      toast.warn("Please Enter Details..");
      return false;
    }
    return true;
  };

  const loadList = async () => {
    if (!finYearId) return toast.warn("Please select Financial Year.");
    if (!employeeId) return toast.warn("Employee not selected (LicEmployeeID missing).");

    try {
      setLoading(true);
      setSelectedIds(new Set());
      const res = await axios.get(`${BASE_URL}/api/license-remarks/list`, {
        params: { finYearId: Number(finYearId), employeeId: Number(employeeId) },
      });
      setRows(res?.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const empCode = (r.fld_EmployeeCode ?? "").toString().toLowerCase();
      const empName = (r.fld_Employee ?? "").toString().toLowerCase();
      const details = (r.fld_Details ?? "").toString().toLowerCase();
      const title = (r.fld_Title ?? "").toString().toLowerCase();
      const dt = (r.RemarkDate ?? "").toString().toLowerCase();
      return empCode.includes(q) || empName.includes(q) || title.includes(q) || details.includes(q) || dt.includes(q);
    });
  }, [rows, search]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filtered.length / pageSize)), [filtered.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const toggleOne = (remarkId) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(remarkId)) n.delete(remarkId);
      else n.add(remarkId);
      return n;
    });
  };

  const toggleAllOnPage = (checked) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      for (const r of paged) {
        const id = Number(r.fld_Remark_ID);
        if (!id) continue;
        if (checked) n.add(id);
        else n.delete(id);
      }
      return n;
    });
  };

  const onDelete = async () => {
    if (selectedIds.size === 0) {
      toast.warn("Please select any one");
      return;
    }
    const ok = window.confirm("Are you sure to delete");
    if (!ok) return;

    try {
      const ids = Array.from(selectedIds);
      const res = await axios.post(`${BASE_URL}/api/license-remarks/delete`, {
        finYearId: Number(finYearId),
        ids,
      });

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Deleted Successfully");
        resetForm();
        await loadList();
      } else {
        toast.error(res?.data?.message || "Delete failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    }
  };

  // ---- edit (load one remark)
  const onEdit = async (row) => {
    const rid = Number(row.fld_Remark_ID);
    if (!rid) return toast.error("Remark ID missing");
    if (!finYearId) return toast.warn("Please select Financial Year.");

    try {
      setSaving(true);
      const res = await axios.get(`${BASE_URL}/api/license-remarks/get`, {
        params: { finYearId: Number(finYearId), remarkId: rid },
      });
      const r = res?.data?.row;
      if (!r) {
        toast.error("Remark not found");
        return;
      }

      setMode("edit");
      setEditingId(rid);
      setTitle(r.fld_Title || "");
      setDetails(r.fld_Details || "");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load remark");
    } finally {
      setSaving(false);
    }
  };

  // ---- add/update submit
  const onSave = async () => {
    if (!validate()) return;

    if (!finYearId) return toast.warn("Please select Financial Year.");
    if (!userId) return toast.warn("Login userId missing.");
    if (!employeeId) return toast.warn("Employee not selected (LicEmployeeID missing).");
    if (!branchId) return toast.warn("branchId missing.");

    const payload = {
      finYearId: Number(finYearId),
      userId: Number(userId),
      branchId: Number(branchId),
      employeeId: Number(employeeId),
      remarkId: mode === "edit" ? Number(editingId) : null,
      title: title.trim(),
      details: details.trim(),
    };

    try {
      setSaving(true);
      const res = await axios.post(`${BASE_URL}/api/license-remarks/save`, payload);

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Plz Retry..");
        return;
      }

      toast.success(res?.data?.message || "Done");
      resetForm();
      await loadList();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Plz Retry..");
    } finally {
      setSaving(false);
    }
  };

  const onBack = () => {
    localStorage.removeItem("LicEmployeeID");
    nav("/payroll/EmployeeLicenseRenewal");
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2800} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE LICENSE REMARKS</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-6xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* ===== Inline ADD/EDIT FORM ===== */}
          <div className={cardBase + " p-6 mb-6"}>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="text-lg font-extrabold">
                {mode === "edit" ? `Edit Remark (ID: ${editingId})` : "Add Remark"}
              </div>

              <div className="flex flex-wrap gap-3">
                <button className={ghostBtn} onClick={onBack}>
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>

                <button className={dangerBtn} onClick={onDelete} disabled={loading || saving}>
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-5">
              <div>
                <label className="font-bold">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  className={inputBase + " mt-2"}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter Title"
                />
              </div>

              <div>
                <label className="font-bold">
                  Details <span className="text-red-500">*</span>
                </label>
                <textarea
                  className={inputBase + " mt-2 min-h-[90px]"}
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder="Enter Details"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
              <button className={primaryBtn} onClick={onSave} disabled={saving}>
                <Save className="w-4 h-4" />
                {mode === "edit" ? "Update" : "Submit"}
              </button>

              <button className={ghostBtn} onClick={resetForm} disabled={saving}>
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>

              {mode === "edit" && (
                <button className={secondaryBtn} onClick={resetForm} disabled={saving}>
                  <XCircle className="w-4 h-4" />
                  Cancel Edit
                </button>
              )}
            </div>
          </div>

          {/* ===== List Controls ===== */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            <div className="text-sm opacity-80">
              EmployeeID: <b>{employeeId || "-"}</b>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 opacity-60" />
                <input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className={inputBase + " pl-11 w-[320px]"}
                  placeholder="Search emp code / name / title / date / remarks..."
                />
              </div>

              <select
                className={inputBase + " w-[140px]"}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>

              <button className={ghostBtn} onClick={loadList} disabled={loading}>
                Refresh
              </button>
            </div>
          </div>

          {/* ===== List Table ===== */}
          <div className={cardBase}>
            <div className="overflow-auto">
              <table className="min-w-[1200px] w-full">
                <thead className={isDark ? "bg-white/10" : "bg-gray-50"}>
                  <tr className="text-sm">
                    <th className="px-4 py-3 text-center w-[80px]">S.No</th>
                    <th className="px-4 py-3 text-center w-[200px]">Employee Code</th>
                    <th className="px-4 py-3 text-center w-[220px]">Employee Name</th>
                    <th className="px-4 py-3 text-center w-[220px]">Title</th>
                    <th className="px-4 py-3 text-center w-[160px]">Date</th>
                    <th className="px-4 py-3 text-center">Remarks</th>
                    <th className="px-4 py-3 text-center w-[120px]">Edit</th>
                    <th className="px-4 py-3 text-center w-[140px]">
                      <div className="flex items-center justify-center gap-2">
                        <input
                          type="checkbox"
                          onChange={(e) => toggleAllOnPage(e.target.checked)}
                          checked={paged.length > 0 && paged.every((r) => selectedIds.has(Number(r.fld_Remark_ID)))}
                        />
                        Select
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : paged.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center">
                        No Records Found!
                      </td>
                    </tr>
                  ) : (
                    paged.map((r, idx) => {
                      const rid = Number(r.fld_Remark_ID);
                      const checked = selectedIds.has(rid);

                      return (
                        <tr key={`${rid}-${idx}`} className={isDark ? "border-t border-white/10" : "border-t border-gray-200"}>
                          <td className="px-4 py-3 text-center">{(page - 1) * pageSize + idx + 1}</td>
                          <td className="px-4 py-3 text-center">{r.fld_Employeecode ?? "-"}</td>
                          <td className="px-4 py-3 text-center">{r.fld_Employee ?? "-"}</td>
                          <td className="px-4 py-3 text-center">{r.fld_title ?? "-"}</td>
                          <td className="px-4 py-3 text-center">{r.RemarkDate ?? "-"}</td>
                          <td className="px-4 py-3 text-center">{r.fld_Details ?? "-"}</td>

                          <td className="px-4 py-3 text-center">
                            <button className={ghostBtn} onClick={() => onEdit(r)} disabled={saving}>
                              <Pencil className="w-4 h-4" />
                              Edit
                            </button>
                          </td>

                          <td className="px-4 py-3 text-center">
                            <input type="checkbox" checked={checked} onChange={() => toggleOne(rid)} />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className={`flex flex-wrap items-center justify-between gap-3 p-4 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
              <div className="text-sm opacity-80">
                {filtered.length === 0 ? (
                  "Showing 0-0 of 0"
                ) : (
                  <>
                    Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, filtered.length)} of {filtered.length}
                  </>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button className={ghostBtn} onClick={() => setPage(1)} disabled={page === 1}>
                  {"<<"}
                </button>
                <button className={ghostBtn} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                  {"<"}
                </button>

                <div className={`px-4 py-2 rounded-2xl text-sm font-bold border ${isDark ? "border-white/10" : "border-gray-200"}`}>
                  Page {page} / {totalPages}
                </div>

                <button className={ghostBtn} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  {">"}
                </button>
                <button className={ghostBtn} onClick={() => setPage(totalPages)} disabled={page === totalPages}>
                  {">>"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
}
